<template>
  <v-dialog
    v-model="dialogModel"
    :width="$vuetify.breakpoint.mdAndUp ? '600px' : ''"
    persistent
  >
    <v-card>
      <v-card-title class="primary accent--text pa-0">
        <v-toolbar
          flat
          color="primary"
        >
          <v-toolbar-title
            class="accent--text headline"
          >
            {{ isEditMode ? 'Update' : 'Create' }} Shift
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>

            <v-btn
              v-if="isEditMode"
              color="accent"
              icon
              @click="onDelete()"
            >
              <v-icon>delete</v-icon>
            </v-btn>
            <v-btn
              color="accent"
              icon
              @click="dialogModel = false"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

      </v-card-title>
      <v-card-text class="pt-5">
        <v-row>
          <v-col cols="12">
            <v-select
              v-model="model.resourceIds"
              label="Resource"
              prepend-icon="star_border"
              :items="resources"
              item-value="id"
              item-text="label"
              :disabled="isEditMode"
              multiple
              small-chips
              deletable-chips
              outlined
              dense
              persistent-hint
              :hint="`${$t('common.divisions.singular')} must be defined in the resource`"
            />
          </v-col>
          <v-col cols="12">
            <v-menu
              ref="dateMenu"
              v-model="dateMenu"
              :close-on-content-click="false"
              :return-value.sync="model.date"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="model.date"
                  label="Date"
                  prepend-icon="event"
                  readonly
                  outlined
                  dense
                  hide-details
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="model.date" no-title scrollable>
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="dateMenu = false">Cancel</v-btn>
                <v-btn text color="primary" @click="$refs.dateMenu.save(model.date)">OK</v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="6">
            <v-menu
              ref="startTimeMenu"
              v-model="startTimeMenu"
              :close-on-content-click="false"
              :return-value.sync="model.startTime"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="model.startTime"
                  label="Start time"
                  prepend-icon="alarm"
                  readonly
                  outlined
                  dense
                  hide-details
                  v-on="on"
                ></v-text-field>
              </template>
              <v-time-picker
                v-model="model.startTime"
                :allowed-minutes="allowedMinutes"
              >
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="startTimeMenu = false">Cancel</v-btn>
                <v-btn text color="primary" @click="$refs.startTimeMenu.save(model.startTime)">
                  OK
                </v-btn>
              </v-time-picker>
            </v-menu>
          </v-col>
          <v-col cols="6">
            <v-menu
              ref="endTimeMenu"
              v-model="endTimeMenu"
              :close-on-content-click="false"
              :return-value.sync="model.endTime"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="model.endTime"
                  label="End time"
                  prepend-icon="alarm"
                  readonly
                  outlined
                  dense
                  hide-details
                  v-on="on"
                ></v-text-field>
              </template>
              <v-time-picker
                v-model="model.endTime"
                :allowed-minutes="allowedMinutes"
              >
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="endTimeMenu = false">Cancel</v-btn>
                <v-btn text color="primary" @click="$refs.endTimeMenu.save(model.endTime)">OK
                </v-btn>
              </v-time-picker>
            </v-menu>
          </v-col>
          <v-col
            v-if="!isEditMode || _get(model, 'recurring.interval', -1) > -1"
            cols="12"
          >
            <v-divider class="mb-3"/>

            <v-select
              v-model="model.recurring.interval"
              :items="recurringIntervals"
              outlined
              dense
              item-value="id"
              item-text="label"
              prepend-icon="autorenew"
              label="Repeats"
              hide-details
            />

            <v-row
              v-if="model.recurring.interval >= 0"
            >
              <v-col cols="12" align="center" justify="center">
                <v-btn-toggle
                  v-model="model.recurring.daysOfWeek"
                  multiple
                  color="primary"

                >
                  <v-btn
                    :value="0"
                    outlined
                  >
                    Sun
                  </v-btn>

                  <v-btn
                    :value="1"
                    outlined
                  >
                    Mon
                  </v-btn>

                  <v-btn
                    :value="2"
                    outlined
                  >
                    Tue
                  </v-btn>

                  <v-btn
                    :value="3"
                    outlined
                  >
                    Wed
                  </v-btn>

                  <v-btn
                    :value="4"
                    outlined
                  >
                    Thu
                  </v-btn>

                  <v-btn
                    :value="5"
                    outlined
                  >
                    Fri
                  </v-btn>

                  <v-btn
                    :value="6"
                    outlined
                  >
                    Sat
                  </v-btn>
                </v-btn-toggle>
              </v-col>
            </v-row>

            <v-row
              v-if="model.recurring.interval > 0"
            >
              <v-col cols="6">
                <v-text-field
                  :value="isEditMode ? model.recurring.startDate : model.date"
                  readonly
                  disabled
                  dense
                  outlined
                  hide-details
                  label="Starts"
                  prepend-icon="event"
                />
              </v-col>
              <v-col cols="6">
                <v-menu
                  ref="endRecurringDateMenu"
                  v-model="endRecurringDateMenu"
                  :close-on-content-click="false"
                  :return-value.sync="model.recurring.endDate"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="model.recurring.endDate"
                      label="Ends"
                      readonly
                      outlined
                      dense
                      hide-details
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="model.recurring.endDate"
                    no-title
                  >
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="endRecurringDateMenu = false">Cancel
                    </v-btn>
                    <v-btn text color="primary"
                           @click="$refs.endRecurringDateMenu.save(model.recurring.endDate)">OK
                    </v-btn>
                  </v-date-picker>
                </v-menu>
              </v-col>
            </v-row>

            <v-divider class="mt-3"/>
          </v-col>

          <v-col cols="12">
<!--            <divisions-drop-down-->
<!--              :multiple="false"-->
<!--              :prepend-icon="'location_on'"-->
<!--              :init-division="model.division"-->
<!--              :disabled="true"-->
<!--              @select-item="selectedDivision"-->
<!--            />-->
            <v-switch
              class="mt-0"
              v-model="model.isPublished"
              inset
              :label="`Published: ${model.isPublished ? 'Yes' : 'No'}`"
              hide-details
            />
          </v-col>

        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer/>
        <v-btn
          :disabled="isLoading"
          rounded
          outlined
          @click="dialogModel = false"
        >
          close
        </v-btn>
        <v-btn
          :loading="isLoading"
          :disabled="isLoading"
          rounded
          outlined
          color="primary"
          @click="onSaveShift"
        >
          save
        </v-btn>
      </v-card-actions>
    </v-card>

    <recurring-shift-confirm-dialog
      :model="showRecurringConfirm"
      :type="recurringConfirmType"
      @cancel="setRecurringConfirm(false, '')"
      @confirm-future="onActionRecurringFutureShifts"
      @confirm-one="onActionRecurringOneShift"
    />

    <confirm-action
      :model="confirmDeleteSingle"
      :color="'error'"
      :title="'Delete Shift'"
      :description="'Are you sure you want to delete this shift?'"
      :action-text="'Delete'"
      @cancel="confirmDeleteSingle = false"
      @confirmed="doDeleteShift()"
    />
  </v-dialog>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import DivisionsDropDown from "../Common/DivisionsDropDown"
import { isEmpty, get } from "lodash"
import { convertTimestampToDate } from "../../services/util.service"
import RecurringShiftConfirmDialog from "./RecurringShiftConfirmDialog"
import ConfirmAction from "../Common/ConfirmAction"

export default {
  components: { ConfirmAction, DivisionsDropDown, RecurringShiftConfirmDialog },
  computed: {
    ...mapGetters([
      'getShiftDialog',
      'getShiftDialogData',
      'getScheduledProducts'
    ]),
    resources() {
      return this.getScheduledProducts
    },
    dialogModel: {
      get() {
        return this.getShiftDialog
      },
      set(val) {
        this.setShiftDialog(val)
      }
    }
  },
  watch: {
    dialogModel(val) {
      if (val === true) {
        this.loadEntity()
      }
    }
  },
  data() {
    return {
      emptyModel: {
        id: null,
        resourceIds: [],
        date: null,
        startTime: null,
        isPublished: false,
        endTime: null,
        startAt: null,
        endAt: null,
        division: null,
        recurring: {
          interval: -1,
          frequency: 'WEEKLY',
          daysOfWeek: [],
          startDate: null,
          endDate: null
        }
      },
      confirmDeleteSingle: false,
      showRecurringConfirm: false,
      recurringConfirmType: '', // edit or delete
      isEditMode: false,
      isLoading: false,
      dateMenu: false,
      startTimeMenu: false,
      endTimeMenu: false,
      endRecurringDateMenu: false,
      recurringIntervals: [
        {
          id: -1,
          label: 'Never'
        },
        {
          id: 0,
          label: 'This week'
        },
        {
          id: 1,
          label: 'Every week'
        },
        {
          id: 2,
          label: 'Every 2 weeks'
        },
        {
          id: 3,
          label: 'Every 3 weeks'
        },
        {
          id: 4,
          label: 'Every 4 weeks'
        },
        {
          id: 5,
          label: 'Every 5 weeks'
        },
        {
          id: 6,
          label: 'Every 6 weeks'
        },
        {
          id: 7,
          label: 'Every 7 weeks'
        },
        {
          id: 8,
          label: 'Every 8 weeks'
        },
      ],
      entity: {},
      model: { ...this.emptyModel }
    }
  },
  created() {
    this.model = { ...this.emptyModel }
  },
  methods: {
    ...mapActions([ 'setShiftDialog', 'ensureShiftEntity', 'createShift', 'updateShift', 'snackError', 'deleteShift' ]),
    _get: (obj, path, def) => get(obj, path, def),
    onDelete() {
      if (get(this.model, 'recurring.interval', -1) === -1) {
        this.confirmDeleteSingle = true
      } else {
        this.setRecurringConfirm(true, 'delete')
      }
    },
    setRecurringConfirm(showDialog, dialogType){
      this.recurringConfirmType = dialogType
      this.showRecurringConfirm = showDialog
    },
    allowedMinutes: m => m % 5 === 0,

    selectedDivision(divisionId) {
      this.model.division = divisionId
    },
    doUpdateShift(modify) {
      this.setRecurringConfirm(false, '')
      const payload = {
        ...this.model,
        modify
      }

      this.isLoading = true
      this.updateShift(payload)
        .then(() => {
          this.setShiftDialog(false)
          this.$emit('shift-change')
        })
        .catch(e => {
          this.snackError(e)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    doDeleteShift(modify) {
      const payload = {
        id: this.model.id,
        modify
      }

      if (get(this.model, 'recurring.interval', -1) === -1) {
        this.deleteShift(payload)
          .then(() => {
            this.setShiftDialog(false)
            this.$emit('shift-change')
          })
          .catch(e => {
            this.snackError(e)
          })
          .finally(() => {
            this.confirmDeleteSingle = false
          })
      } else {
        this.isLoading = true
        this.deleteShift(payload)
          .then(() => {
            this.setShiftDialog(false)
            this.$emit('shift-change')
          })
          .catch(e => {
            this.snackError(e)
          })
          .finally(() => {
            this.isLoading = false
          })
      }
    },
    onActionRecurringOneShift(type) {
      this.setRecurringConfirm(false)

      if (type === 'edit') {
        this.doUpdateShift()
      } else if (type === 'delete') {
        this.doDeleteShift()
      }
    },
    onActionRecurringFutureShifts(type) {
      this.setRecurringConfirm(false)

      if (type === 'edit') {
        this.doUpdateShift('future')
      } else if (type === 'delete') {
        this.doDeleteShift('future')
      }
    },
    onSaveShift() {
      const payload = {
        ...this.model,
      }

      if (this.isEditMode) {
        /**
         * IMPORTANT
         * Check the compound ID; If it changes from the date selected
         * there is no need to show the edit recurring window because it will create a single shift
         */

        if (get(this.model, 'recurring.interval', -1) === -1) {
          this.doUpdateShift()
        } else {
          this.setRecurringConfirm(true, 'edit')
        }
      } else {
        this.isLoading = true
        this.createShift(payload)
          .then(() => {
            this.setShiftDialog(false)
            this.$emit('shift-change')
          })
          .catch(e => {
            this.snackError(e)
          })
          .finally(() => {
            this.isLoading = false
          })
      }
    },
    loadEntity() {
      const dialogData = this.getShiftDialogData
      const { resourceIds, date, type, shift } = dialogData

      this.model = { ...this.emptyModel }

      this.isEditMode = !isEmpty(shift)
      const isRecurring = !isEmpty(get(shift, 'recurring'))

      const setDates = () => {
        // Need to convert our datetime format into time and date
        const startAtDate = convertTimestampToDate(shift.startAt)
        const endAtDate = convertTimestampToDate(shift.endAt)

        this.model.startTime = moment(startAtDate).format('HH:mm')
        this.model.endTime = moment(endAtDate).format('HH:mm')
        this.model.date = moment(startAtDate).format('YYYY-MM-DD')

        if (isRecurring) {
          const recStartAtDate = convertTimestampToDate(shift.recurring.startAt)
          const recEndAtDate = convertTimestampToDate(shift.recurring.endAt)

          this.model.recurring.startDate = moment(recStartAtDate).format('YYYY-MM-DD')
          this.model.recurring.endDate = moment(recEndAtDate).format('YYYY-MM-DD')
        }
      }

      if (this.isEditMode) {
        this.model = {
          ...shift,
          recurring: isRecurring
            ? { ...shift.recurring }
            : { ...this.emptyModel.recurring }
        }
        this.model.resourceIds = shift.meta.id

        setDates()
      } else {
        this.ensureShiftEntity()
          .then(res => {
            // product or user
            this.entity = res
            this.model.date = moment(date).format('YYYY-MM-DD')
            this.model.resourceIds = [ ...resourceIds ]
            this.model.division = this.entity.division

            // Pre-populate the start and end times for ease of use
            this.model.startTime = '09:00'
            this.model.endTime = '17:00'
          })
      }
    },
  }
}
</script>
